import { Link } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle2, Building2, Lock, Users } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = '/insolvio-logo-cropped.png';

// Trust Center content - Swedish
const trustContentSv = {
  eyebrow: 'TRUST CENTER',
  title: 'Byggt för förtroende.',
  intro: 'Här ser du hur Insolvio hanterar kundinformation och hur vi uppfyller de krav som ställs på externa IT-tjänster.',
  caseFlow: {
    eyebrow: 'VAD SOM GÖRS OCH HUR',
    title: 'Så hanteras ett ärende i Insolvio',
    subtitle: 'Från första handlingen till arkivering ligger informationen i byråns egen avgränsade miljö i Sverige.',
    steps: [
      { num: '1', title: 'Skapa ärende', desc: 'Konkurs eller rekonstruktion startas med en avgränsad ärendemiljö för byrån.' },
      { num: '2', title: 'Arbeta och dokumentera', desc: 'Förvaltaren och medarbetarna arbetar med behörighet per roll och ärende.' },
      { num: '3', title: 'Rapportera och arkivera', desc: 'Rapporter tas fram och hela ärendet kan exporteras för arkivering hos byrån.' },
    ],
  },
  dataFlow: {
    title: 'Vart informationen tar vägen',
    cards: [
      { icon: Building2, title: 'Byrån', subtitle: 'Du äger informationen', desc: 'Klientinformation, rättigheter och ärendedata tillhör byrån. Inget av detta övergår till Insolvio.' },
      { icon: Lock, title: 'Insolvio', subtitle: 'Krypterad och avgränsad', desc: 'Lagring i Sverige, kryptering i transit, vila och bearbetning, åtkomststyrning per roll, loggning och säkerhetskopiering.' },
      { icon: Users, title: 'Underleverantörer', subtitle: 'Bara det som behövs', desc: 'Hosting och andra tjänster som är nödvändiga för driften, är alla bundna av avtal.' },
    ],
  },
  subprocessors: {
    eyebrow: 'UNDERLEVERANTÖRER',
    title: 'Vilka som hanterar data, och var',
    subtitle: 'Alla underleverantörer är bundna av avtal med motsvarande sekretess- och säkerhetskrav. Vi meddelar kunder 30 dagar innan en ny underleverantör läggs till.',
    headers: ['Leverantör', 'Ändamål', 'Region', 'Skydd'],
    rows: [
      ['Upcloud Oy', 'Drift och lagring av plattformen', 'Sverige', 'Kryptering, biträdesavtal, åtkomstbegränsning'],
      ['E-post och e-signering', 'Utskick av systemmeddelanden och digital signatur', 'EU', 'Biträdesavtal, inget klientinnehåll i meddelanden'],
    ],
  },
  incidents: {
    eyebrow: 'INCIDENTER',
    title: 'Om något händer',
    steps: [
      { num: '1', title: 'Upptäckt och avgränsning', desc: 'Övervakning och larm. Vi begränsar händelsen och säkrar spår.' },
      { num: '2', title: 'Information till kund', desc: 'Vi berättar vad som hänt, vilken information som berörs och vad vi gör.' },
      { num: '3', title: 'Åtgärd och återställning', desc: 'Drift återupptas enligt våra återställningsmål.' },
      { num: '4', title: 'Slutrapport', desc: 'Orsak, påverkan och åtgärder för att förhindra upprepning.' },
    ],
  },
  exit: {
    eyebrow: 'AVTALETS SLUT',
    title: 'Så lämnar du Insolvio',
    steps: [
      { num: '1', title: 'Export', desc: 'Hela ärendet och dess handlingar kan enkelt exporteras i ett vanligt förekommande format.' },
      { num: '2', title: 'Radering', desc: 'Klientinformation raderas, inklusive dubbletter, kopior och backup inom 90 dagar.' },
      { num: '3', title: 'Bekräftelse', desc: 'Du får en skriftlig bekräftelse på att radering är genomförd.' },
      { num: '4', title: 'Anonymiserad meta- och loggdata', desc: 'Eventuell kvarvarande data kan inte kopplas till byrån, personalen eller klienterna.' },
    ],
  },
  responsibility: {
    eyebrow: 'DELAT ANSVAR',
    title: 'Vem ansvarar för vad',
    subtitle: 'Varje byrå gör sin egen riskbedömning. Så här fördelas ansvaret mellan oss.',
    insolvio: {
      title: 'Insolvio ansvarar för',
      items: [
        'Drift, säkerhet och tillgänglighet i plattformen.',
        'Avtal med underleverantörer och löpande uppföljning av dem.',
        'Information vid incidenter och myndighetskrav.',
        'Export, radering och stöd vid avtalets slut.',
        'Öppen redovisning här i Trust Center.',
      ],
    },
    firm: {
      title: 'Byrån ansvarar för',
      items: [
        'Egen riskbedömning av tjänsten, som ses över löpande.',
        'Bedömning av om klienter ska informeras eller samtycke inhämtas.',
        'Egen kontinuitetsplanering och intern användarreglering.',
        'Hantering av användare, roller och lösenord hos byrån.',
        'Att besluta om AI-funktioner ska användas.',
      ],
    },
  },
  documents: {
    eyebrow: 'DOKUMENT',
    title: 'Underlag för due diligence',
    items: [
      { title: 'Allmänna villkor och personuppgiftsbiträdesavtal', desc: 'Avtalsvillkor för tjänsten inbegripet personuppgiftsbiträdesavtal.' },
      { title: 'Integritetsmeddelande', desc: 'Hur vi behandlar personuppgifter när du kontaktar oss, bokar en demo eller använder plattformen.', href: '/documents/integritetsmeddelande-insolvio.pdf' },
      { title: 'Säkerhetsbeskrivning', desc: 'Arkitektur, kryptering, åtkomst, loggning och kontinuitet.' },
    ],
  },
  backToHome: 'Tillbaka till startsidan',
  download: 'Ladda ner',
};

// Trust Center content - English (translated)
const trustContentEn = {
  eyebrow: 'TRUST CENTER',
  title: 'Built on trust.',
  intro: 'Here you can see how Insolvio handles customer information and how we meet the requirements for external IT services.',
  caseFlow: {
    eyebrow: 'WHAT WE DO AND HOW',
    title: 'How a case is handled in Insolvio',
    subtitle: 'From the first document to archiving, information remains in the firm\'s own isolated environment in Sweden.',
    steps: [
      { num: '1', title: 'Create case', desc: 'Bankruptcy or reconstruction starts with an isolated case environment for the firm.' },
      { num: '2', title: 'Work and document', desc: 'The administrator and staff work with role-based access per case.' },
      { num: '3', title: 'Report and archive', desc: 'Reports are produced and the entire case can be exported for archiving at the firm.' },
    ],
  },
  dataFlow: {
    title: 'Where the information goes',
    cards: [
      { icon: Building2, title: 'The firm', subtitle: 'You own the information', desc: 'Client information, rights and case data belong to the firm. None of this transfers to Insolvio.' },
      { icon: Lock, title: 'Insolvio', subtitle: 'Encrypted and isolated', desc: 'Storage in Sweden, encryption in transit, at rest and during processing, role-based access control, logging and backups.' },
      { icon: Users, title: 'Subprocessors', subtitle: 'Only what is needed', desc: 'Hosting and other services necessary for operation are all bound by contracts.' },
    ],
  },
  subprocessors: {
    eyebrow: 'SUBPROCESSORS',
    title: 'Who handles data, and where',
    subtitle: 'All subprocessors are bound by contracts with equivalent confidentiality and security requirements. We notify customers 30 days before adding a new subprocessor.',
    headers: ['Provider', 'Purpose', 'Region', 'Safeguards'],
    rows: [
      ['Upcloud Oy', 'Operation and storage of the platform', 'Sweden', 'Encryption, DPA, access restriction'],
      ['Email and e-signature', 'Distribution of system messages and digital signatures', 'EU', 'DPA, no client content in messages'],
    ],
  },
  incidents: {
    eyebrow: 'INCIDENTS',
    title: 'If something happens',
    steps: [
      { num: '1', title: 'Detection and containment', desc: 'Monitoring and alerts. We contain the event and secure evidence.' },
      { num: '2', title: 'Customer notification', desc: 'We tell you what happened, what information is affected and what we are doing.' },
      { num: '3', title: 'Action and recovery', desc: 'Operations resume according to our recovery targets.' },
      { num: '4', title: 'Final report', desc: 'Cause, impact and measures to prevent recurrence.' },
    ],
  },
  exit: {
    eyebrow: 'END OF AGREEMENT',
    title: 'How to leave Insolvio',
    steps: [
      { num: '1', title: 'Export', desc: 'The entire case and its documents can easily be exported in a commonly used format.' },
      { num: '2', title: 'Deletion', desc: 'Client information is deleted, including duplicates, copies and backups within 90 days.' },
      { num: '3', title: 'Confirmation', desc: 'You receive written confirmation that deletion has been performed.' },
      { num: '4', title: 'Anonymized meta and log data', desc: 'Any remaining data cannot be linked to the firm, staff or clients.' },
    ],
  },
  responsibility: {
    eyebrow: 'SHARED RESPONSIBILITY',
    title: 'Who is responsible for what',
    subtitle: 'Each firm conducts its own risk assessment. This is how responsibility is divided between us.',
    insolvio: {
      title: 'Insolvio is responsible for',
      items: [
        'Operation, security and availability of the platform.',
        'Agreements with subprocessors and ongoing monitoring of them.',
        'Information in case of incidents and authority requirements.',
        'Export, deletion and support at end of agreement.',
        'Open reporting here in the Trust Center.',
      ],
    },
    firm: {
      title: 'The firm is responsible for',
      items: [
        'Its own risk assessment of the service, reviewed on an ongoing basis.',
        'Assessing whether clients should be informed or consent obtained.',
        'Its own continuity planning and internal user regulation.',
        'Management of users, roles and passwords at the firm.',
        'Deciding whether AI features should be used.',
      ],
    },
  },
  documents: {
    eyebrow: 'DOCUMENTS',
    title: 'Due diligence materials',
    items: [
      { title: 'General terms and Data Processing Agreement', desc: 'Terms of service including Data Processing Agreement.' },
      { title: 'Privacy Notice', desc: 'How we process personal data when you contact us, book a demo or use the platform.', href: '/documents/integritetsmeddelande-insolvio.pdf' },
      { title: 'Security description', desc: 'Architecture, encryption, access, logging and continuity.' },
    ],
  },
  backToHome: 'Back to Home',
  download: 'Download',
};

export default function TrustCenterPage() {
  const { language } = useLanguage();
  const content = language === 'sv' ? trustContentSv : trustContentEn;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fdfdfd' }} data-testid="trust-center-page">
      {/* Header */}
      <header style={{ borderBottom: '1px solid #e5e5e5', backgroundColor: '#fdfdfd' }}>
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="trust-logo">
              <img src={LOGO_URL} alt="Insolvio" className="h-8 md:h-10" style={{ filter: 'invert(1)' }} />
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link
                to="/"
                className="flex items-center gap-2 transition-colors text-sm"
                style={{ color: '#6b6b6b' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1c8a56'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b6b6b'}
                data-testid="trust-back-link"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">{content.backToHome}</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="eyebrow mb-4" style={{ color: '#1c8a56' }}>
            {content.eyebrow}
          </p>
          <h1 
            className="mb-6"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: 700,
              color: '#262626',
              letterSpacing: '-0.01em',
              lineHeight: '1.1'
            }}
            data-testid="trust-title"
          >
            {content.title}
          </h1>
          <p 
            className="max-w-2xl mx-auto"
            style={{ color: '#6b6b6b', fontSize: '18px', lineHeight: '1.7', letterSpacing: '0.025em' }}
          >
            {content.intro}
          </p>
        </div>
      </section>

      {/* Case Flow */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3" style={{ color: '#1c8a56' }}>{content.caseFlow.eyebrow}</p>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626', marginBottom: '12px' }}>
              {content.caseFlow.title}
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#6b6b6b', fontSize: '16px', lineHeight: '1.6', letterSpacing: '0.025em' }}>
              {content.caseFlow.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {content.caseFlow.steps.map((step) => (
              <div key={step.num} className="rounded-lg p-6" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4 font-semibold" style={{ backgroundColor: '#1c8a56', color: '#ffffff' }}>
                  {step.num}
                </div>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 600, color: '#262626', marginBottom: '8px' }}>
                  {step.title}
                </h3>
                <p style={{ color: '#6b6b6b', fontSize: '15px', lineHeight: '1.6', letterSpacing: '0.025em' }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Flow */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626' }}>
            {content.dataFlow.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {content.dataFlow.cards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className="rounded-lg p-6" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
                  <div className="w-11 h-11 rounded-md flex items-center justify-center mb-4" style={{ backgroundColor: '#eeeeee' }}>
                    <Icon className="w-5 h-5" style={{ color: '#1c8a56' }} strokeWidth={2} />
                  </div>
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 600, color: '#262626', marginBottom: '4px' }}>
                    {card.title}
                  </h3>
                  <p className="mb-3" style={{ color: '#1c8a56', fontSize: '14px', fontWeight: 500, letterSpacing: '0.025em' }}>
                    {card.subtitle}
                  </p>
                  <p style={{ color: '#6b6b6b', fontSize: '15px', lineHeight: '1.6', letterSpacing: '0.025em' }}>
                    {card.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Subprocessors */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="eyebrow mb-3" style={{ color: '#1c8a56' }}>{content.subprocessors.eyebrow}</p>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626', marginBottom: '12px' }}>
              {content.subprocessors.title}
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#6b6b6b', fontSize: '16px', lineHeight: '1.6', letterSpacing: '0.025em' }}>
              {content.subprocessors.subtitle}
            </p>
          </div>
          <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid #e5e5e5' }}>
            <table className="min-w-full text-sm">
              <thead style={{ backgroundColor: '#f5f5f5' }}>
                <tr>
                  {content.subprocessors.headers.map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left font-semibold" style={{ color: '#262626', letterSpacing: '0.025em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {content.subprocessors.rows.map((row, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #e5e5e5' }}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-4 py-3" style={{ color: '#4a4a4a', letterSpacing: '0.025em' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Incidents */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="eyebrow mb-3" style={{ color: '#1c8a56' }}>{content.incidents.eyebrow}</p>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626' }}>
              {content.incidents.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {content.incidents.steps.map((step) => (
              <div key={step.num} className="rounded-lg p-5 flex gap-4" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
                <div className="w-9 h-9 flex-shrink-0 rounded-md flex items-center justify-center font-semibold" style={{ backgroundColor: '#1c8a56', color: '#ffffff' }}>
                  {step.num}
                </div>
                <div>
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '4px' }}>
                    {step.title}
                  </h3>
                  <p style={{ color: '#6b6b6b', fontSize: '14px', lineHeight: '1.5', letterSpacing: '0.025em' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exit */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="eyebrow mb-3" style={{ color: '#1c8a56' }}>{content.exit.eyebrow}</p>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626' }}>
              {content.exit.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {content.exit.steps.map((step) => (
              <div key={step.num} className="rounded-lg p-5 flex gap-4" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
                <div className="w-9 h-9 flex-shrink-0 rounded-md flex items-center justify-center font-semibold" style={{ backgroundColor: '#1c8a56', color: '#ffffff' }}>
                  {step.num}
                </div>
                <div>
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '4px' }}>
                    {step.title}
                  </h3>
                  <p style={{ color: '#6b6b6b', fontSize: '14px', lineHeight: '1.5', letterSpacing: '0.025em' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shared Responsibility */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="eyebrow mb-3" style={{ color: '#1c8a56' }}>{content.responsibility.eyebrow}</p>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626', marginBottom: '12px' }}>
              {content.responsibility.title}
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#6b6b6b', fontSize: '16px', lineHeight: '1.6', letterSpacing: '0.025em' }}>
              {content.responsibility.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[content.responsibility.insolvio, content.responsibility.firm].map((group, i) => (
              <div key={i} className="rounded-lg p-6" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 600, color: '#262626', marginBottom: '16px' }}>
                  {group.title}
                </h3>
                <ul className="space-y-3">
                  {group.items.map((item, j) => (
                    <li key={j} className="flex gap-3" style={{ color: '#4a4a4a', fontSize: '15px', lineHeight: '1.5', letterSpacing: '0.025em' }}>
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1c8a56' }} strokeWidth={2} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-16 px-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="eyebrow mb-3" style={{ color: '#1c8a56' }}>{content.documents.eyebrow}</p>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(26px, 3.5vw, 36px)', fontWeight: 600, color: '#262626' }}>
              {content.documents.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {content.documents.items.map((item, i) => (
              <div key={i} className="rounded-lg p-6" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e5e5' }}>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p className="mb-4" style={{ color: '#6b6b6b', fontSize: '14px', lineHeight: '1.5', letterSpacing: '0.025em' }}>
                  {item.desc}
                </p>
                {item.href ? (
                  <a
                    href={item.href}
                    download
                    className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
                    style={{ color: '#1c8a56' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    data-testid={`document-download-${i}`}
                  >
                    <Download className="w-4 h-4" strokeWidth={2} />
                    {content.download}
                  </a>
                ) : (
                  <button
                    className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
                    style={{ color: '#1c8a56' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    data-testid={`document-download-${i}`}
                  >
                    <Download className="w-4 h-4" strokeWidth={2} />
                    {content.download}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ borderTop: '1px solid #e5e5e5' }}>
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-sm text-center" style={{ color: '#6b6b6b', letterSpacing: '0.025em' }}>
            Copyright © 2026 | Insolvio AB
          </p>
        </div>
      </footer>
    </div>
  );
}
